import { Link } from "react-router-dom";
import { MessageCircle, Phone, Mail, MapPin } from "lucide-react";
import { useLang } from "../lib/lang";
import { PHONE, WA_DEFAULT, DISCLAIMER } from "../lib/copy";
import { Brand, brandify } from "./Brand";
import { BUSINESS } from "../config/business";
import { trackWhatsApp, trackCall } from "../lib/analytics";
import { openCookieSettings } from "./ConsentBanner";
import { Logo } from "./Logo";
import { Reveal } from "./Reveal";

export const CtaBand = () => {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden bg-ink py-24 text-white lg:py-32" data-testid="cta-band">
      <div className="dot-grid pointer-events-none absolute inset-0 opacity-30" aria-hidden="true" />
      <div className="relative mx-auto max-w-5xl px-5 sm:px-8 sm:text-center">
        <Reveal><h2 className="font-display text-3xl font-extrabold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">{t.cta.title}</h2></Reveal>
        <a href={`tel:+91${PHONE}`} onClick={() => trackCall("cta-band")} data-testid="cta-band-phone-number" className="mt-10 block font-display text-5xl font-extrabold tracking-tight text-sun sm:text-7xl lg:text-8xl">{PHONE}</a>
        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("cta-band")} data-testid="cta-band-whatsapp-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-sun px-8 py-4 font-display font-extrabold text-night hover:bg-[#ffb63d]"><MessageCircle className="h-5 w-5" aria-hidden="true" />{t.cta.button}</a>
          <a href={`tel:+91${PHONE}`} onClick={() => trackCall("cta-band-button")} data-testid="cta-band-call-button" className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 font-display font-extrabold text-ink hover:bg-sky"><Phone className="h-5 w-5" aria-hidden="true" />{t.cta.call}</a>
        </div>
      </div>
    </section>
  );
};

const QUICK = [["/programmes", "programs"], ["/programmes#universities", "universities"], ["/programmes#how-it-works", "how"], ["/programmes#verify", "verify"], ["/contact#faq", "faq"], ["/contact", "contact"]];
const LEGAL = [["/privacy", "Privacy Policy"], ["/terms", "Terms of Use"], ["/cookies", "Cookie Policy"], ["/disclaimer", "Disclaimer"]];

export const Footer = () => {
  const { t } = useLang();
  const f = t.footer;
  return (
    <footer className="bg-ink pt-20 text-white/90" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 pb-12 sm:px-8 md:grid-cols-2 lg:grid-cols-3">
        <div>
          <Logo size="lg" testId="footer-logo" />
          <p className="mt-6 text-base leading-relaxed">{f.desc}</p>
        </div>
        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-sun">{f.quick}</h3>
          <ul className="mt-5 space-y-3"><li><Link to="/about" className="font-display font-bold hover:text-white" data-testid="footer-link-about">{t.nav.about}</Link></li>{QUICK.map(([to, k]) => <li key={k}><Link to={to} className="font-display font-bold hover:text-white" data-testid={`footer-link-${k}`}>{t.nav[k]}</Link></li>)}</ul>
          <h3 className="mt-8 font-display text-sm font-bold uppercase tracking-[0.2em] text-sun">{f.legal}</h3>
          <ul className="mt-5 space-y-3">{LEGAL.map(([to, label]) => <li key={to}><Link to={to} className="hover:text-white" data-testid={`footer-legal-${to.slice(1)}`}>{label}</Link></li>)}
            <li><button onClick={openCookieSettings} className="hover:text-white" data-testid="footer-cookie-settings">{f.cookies}</button></li></ul>
        </div>
        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-sun">{f.contact}</h3>
          <ul className="mt-5 space-y-4">
            <li><a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("footer")} className="flex items-center gap-3 hover:text-white" data-testid="footer-whatsapp-link"><MessageCircle className="h-5 w-5 text-sun" aria-hidden="true" />{f.whatsapp}: {PHONE}</a></li>
            <li><a href={`tel:+91${PHONE}`} onClick={() => trackCall("footer")} className="flex items-center gap-3 hover:text-white" data-testid="footer-phone-link"><Phone className="h-5 w-5 text-sun" aria-hidden="true" />{f.phone}: {PHONE}</a></li>
            <li><a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-3 break-all hover:text-white" data-testid="footer-email-link"><Mail className="h-5 w-5 shrink-0 text-sun" aria-hidden="true" />{f.email}: {BUSINESS.email}</a></li>
            <li className="flex items-start gap-3" data-testid="footer-address"><MapPin className="mt-0.5 h-5 w-5 shrink-0 text-sun" aria-hidden="true" />{BUSINESS.address}</li>
          </ul>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-5 sm:px-8"><p className="rounded-2xl border border-white/15 p-5 text-sm leading-relaxed" data-testid="footer-disclaimer">{brandify(DISCLAIMER)}</p></div>
      <div className="mt-8 border-t border-white/10">
        <p className="mx-auto max-w-7xl px-5 py-6 pb-28 text-sm sm:px-8 sm:pb-6" data-testid="footer-copyright">© {new Date().getFullYear()} <Brand />. {f.rights}</p>
      </div>
    </footer>
  );
};

export const FloatingButtons = () => {
  const { t } = useLang();
  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3">
      <a href={`tel:+91${PHONE}`} onClick={() => trackCall("floating")} aria-label={`Call ${PHONE}`} data-testid="floating-call-button" className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-ink shadow-[0_10px_30px_-8px_rgba(18,24,63,0.5)] md:hidden"><Phone className="h-5 w-5" aria-hidden="true" /></a>
      <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("floating")} data-testid="whatsapp-floating-button"
        className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3.5 font-display text-sm font-extrabold text-white shadow-[0_14px_34px_-10px_rgba(18,24,63,0.8)] ring-2 ring-sun hover:bg-night">
        <MessageCircle className="h-5 w-5 text-sun" aria-hidden="true" />{t.float}
      </a>
    </div>
  );
};
